import type { AutocompleteInputChangeReason } from "@mui/material/useAutocomplete";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import type React from "react";
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useDebouncedNormalizedFloatInput } from "../../../../components/UIToolkit/Inputs/hooks/useDebouncedNormalizedFloatInput";
import { useAuth } from "../../../../contexts/AuthProvider";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import { useDepartmentNurseTypes } from "../../../../hooks/useDepartmentNurseTypes";
import { useRevealWage } from "../../../../hooks/useRevealWage";
import { useRoleColors } from "../../../../hooks/useRoleColors";
import { backendService } from "../../../../services/backend.service";
import { trackEvent } from "../../../../utils/analytics";
import { hasHrisIntegrationOfType } from "../../../../utils/hrisIntegrations";
import { type AgencyWage, type BaseWage, EmploymentType, type Nurse, NurseFullRole, NurseTrainingStatus, WageType } from "../../../../utils/models";
import axiosInstance from "../../../../utils/networkWrapper";
import { getEnumKeyByValue, inverseRolesMap, isAgencyNurse } from "../../../../utils/utilFunctions";
import { useGetDepartmentNurseUnions } from "../hooks";
import { isUnionMemberInEditState } from "../util/constants";
import {
	getNurseEmploymentDropdownItems,
	getNurseRolesDropdownItems,
	type NurseEmploymentTypeKeys,
	type NurseFullRoleKeys,
	type NurseWageTypeKeys,
	nurseTrainingStatusDropdownItems,
	nurseWageTypeDropdownItems,
	type TrainingStatusKeys,
} from "./Dropdowns";
import { applyRecommendedJobs, resolveRecommendedJobs } from "./recommendedJobs";
import { useAgencies } from "./useAgencies";
import { useJobRecommendations } from "./useJobRecommendations";

type Props = {
	nurse: Nurse | undefined;
	setNurseData: Dispatch<SetStateAction<Nurse>>;
	isEditable: boolean;
};
export const useProfessionalInfoState = ({ nurse, setNurseData, isEditable }: Props) => {
	const roleColor = useRoleColors();
	const { chosenDepartment } = useChosenDepartment();
	const { isSnf } = useDepartmentFlags();
	const departmentNurseTypes = useDepartmentNurseTypes(chosenDepartment.department_id);
	const { userInfo } = useAuth();
	const canViewWage = useRevealWage();
	const { track, trackError } = useAnalytics();
	const { agencies, createAgency } = useAgencies();
	const { data: existingUnions, isLoading: existingUnionsLoading } = useGetDepartmentNurseUnions();
	const [agencyForWageFetch, setAgencyForWageFetch] = useState<{
		agencyName: string;
		role: string;
	} | null>(null);
	const [showJobReminderDialog, setShowJobReminderDialog] = useState(false);

	useEffect(() => {
		return () => {
			setAgencyForWageFetch(null);
		};
	}, []);

	const availableRoles = useMemo(() => {
		return getNurseRolesDropdownItems(chosenDepartment, roleColor).filter((value) => departmentNurseTypes.includes(NurseFullRole[value.value]));
	}, [departmentNurseTypes, chosenDepartment, roleColor]);

	const departmentDefaultRole = useMemo(() => chosenDepartment.default_role as NurseFullRoleKeys | undefined, [chosenDepartment.default_role]);

	const selectedRole: NurseFullRoleKeys | undefined = useMemo(() => {
		if (nurse?.role) {
			const current = inverseRolesMap[nurse.role];
			return availableRoles.find((item) => item.value === current)?.value;
		}

		// New nurse (no stored role yet): seed the dropdown from the department default.
		if (departmentDefaultRole) {
			const match = availableRoles.find((item) => item.value === departmentDefaultRole);
			if (match) return match.value;
		}

		return undefined;
	}, [nurse, availableRoles, departmentDefaultRole]);

	const { data: agencyWages, isFetching: fetchingAgencyWage } = useQuery({
		queryKey: ["agency-wages", chosenDepartment.department_id, agencyForWageFetch?.agencyName, agencyForWageFetch?.role],
		queryFn: async () => {
			const response = await axiosInstance.get<AgencyWage[]>(`/agency-wages/${chosenDepartment.department_id}`);
			return response.data;
		},
		enabled: !!agencyForWageFetch && !!agencyForWageFetch.agencyName && !!agencyForWageFetch.role && isEditable,
		staleTime: 30000,
	});

	useEffect(() => {
		if (!agencyWages || !agencyForWageFetch?.agencyName || !agencyForWageFetch?.role) return;

		const matchingWage = agencyWages.find((w) => w.agencyName === agencyForWageFetch.agencyName && w.role === agencyForWageFetch.role);

		let newWage: number | undefined;
		if (matchingWage?.wage && matchingWage.wage > 0) {
			newWage = matchingWage.wage;
		} else {
			newWage = undefined;
		}

		setNurseData((prevState) => {
			// Prevent infinite loop - only update if wage actually changed
			if (prevState.wage === newWage) {
				return prevState;
			}

			// Only auto-populate wage for agency employment types
			if (!isAgencyNurse(prevState.employmentType)) {
				return prevState;
			}

			if (newWage === undefined) {
				return {
					...prevState,
					wage: undefined,
					baseWages: [],
				};
			}

			// Otherwise update with the new wage
			return {
				...prevState,
				wage: newWage,
				baseWages:
					prevState.baseWages.length > 0
						? prevState.baseWages.map((currWage) => (!currWage.endTime ? { ...currWage, wage: newWage } : currWage))
						: [
								{
									wage: newWage,
									wageType: WageType.Hourly,
									startTime: DateTime.now(),
								},
							],
			};
		});
	}, [agencyWages, agencyForWageFetch?.agencyName, agencyForWageFetch?.role, setNurseData]);

	const onRoleChange = useCallback(
		(newRole?: NurseFullRoleKeys) => {
			if (!newRole) return;

			const previousRole = nurse?.role;
			setNurseData((prevState) => ({
				...prevState,
				role: NurseFullRole[newRole],
			}));

			if (nurse?.agencyName && nurse.employmentType && isAgencyNurse(nurse.employmentType)) {
				setAgencyForWageFetch({
					agencyName: nurse.agencyName,
					role: NurseFullRole[newRole],
				});
			}

			const isExistingProfile = !!nurse?.id;
			if (isExistingProfile && previousRole && previousRole !== NurseFullRole.UDF && previousRole !== NurseFullRole[newRole]) {
				setShowJobReminderDialog(true);
			}

			trackEvent("profile_role_change", {
				role: newRole,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.role, nurse?.id, nurse?.agencyName, nurse?.employmentType],
	);

	const selectedContractType: NurseEmploymentTypeKeys = useMemo(
		() =>
			nurse
				? (
						getNurseEmploymentDropdownItems(isSnf).find((value) => value.value === getEnumKeyByValue(EmploymentType, nurse.employmentType)) ??
						getNurseEmploymentDropdownItems(isSnf)[0]
					).value
				: getNurseEmploymentDropdownItems(isSnf)[0].value,
		[nurse, isSnf],
	);
	const onContractTypeChange = useCallback(
		(newContractType?: NurseEmploymentTypeKeys) => {
			if (newContractType) {
				const newEmploymentType = EmploymentType[newContractType];

				setNurseData((prevState) => ({
					...prevState,
					employmentType: newEmploymentType,
					agencyName: isAgencyNurse(newEmploymentType) ? prevState.agencyName : undefined,
					wageType: isAgencyNurse(newEmploymentType) ? WageType.Hourly : prevState.wageType,
					baseWages: isAgencyNurse(newEmploymentType)
						? prevState.baseWages.map((wage) => (!wage.endTime ? { ...wage, wageType: WageType.Hourly } : wage))
						: prevState.baseWages,
				}));

				if (!isAgencyNurse(newEmploymentType)) {
					setAgencyForWageFetch(null);
				}
			}
			trackEvent("profile_contract_type_change", {
				contract_type: newContractType,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id],
	);

	const baseWage: BaseWage | undefined = useMemo(() => nurse?.baseWages.find((baseWage) => !baseWage.endTime), [nurse]);

	const selectedWageType: NurseWageTypeKeys | undefined = useMemo(
		() =>
			baseWage
				? (nurseWageTypeDropdownItems.find((value) => baseWage.wageType && value.value === getEnumKeyByValue(WageType, baseWage.wageType)) ?? undefined)?.value
				: undefined,
		[baseWage],
	);
	const onWageTypeChange = useCallback(
		(newWageType?: NurseWageTypeKeys) => {
			if (newWageType) {
				setNurseData((prevState) => {
					if (prevState.baseWages.length === 0) {
						return {
							...prevState,
							baseWages: [
								{
									wage: 0,
									wageType: WageType[newWageType],
									startTime: DateTime.now(),
								},
							],
							wageType: WageType[newWageType],
						};
					}

					return {
						...prevState,
						baseWages: prevState.baseWages.map((wage) => (!wage.endTime ? { ...wage, wageType: WageType[newWageType] } : wage)),
						wageType: WageType[newWageType],
					};
				});
			}
			trackEvent("profile_wage_type_change", {
				wage_type: newWageType,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id],
	);

	const setValueUpdateForWage = useCallback(
		(wage: number) => {
			setNurseData((prevState) => ({
				...prevState,
				baseWages: prevState.baseWages.map((currWage) => (!currWage.endTime ? { ...currWage, wage } : currWage)),
				wage: wage,
			}));
		},
		[setNurseData],
	);

	const { onInputChange: onWageChange, numberFinalizedValue: wageValue } = useDebouncedNormalizedFloatInput({
		setValueUpdate: setValueUpdateForWage,
		numericValue: baseWage?.wage,
	});

	const prnShiftsInCycle = useMemo(() => {
		if (!nurse || !nurse.prnShiftsInCycle) return "0";
		return nurse.prnShiftsInCycle.toFixed(0);
	}, [nurse]);
	const onPrnShiftsInCycleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const prnShiftsInCycle = Number.parseInt(event.target.value);
			if (!Number.isNaN(prnShiftsInCycle) && prnShiftsInCycle > 0) {
				setNurseData((prevState) => ({
					...prevState,
					prnShiftsInCycle: prnShiftsInCycle,
				}));
			} else {
				setNurseData((prevState) => ({
					...prevState,
					prnShiftsInCycle: 0,
				}));
			}
		},
		[setNurseData],
	);

	const prnWeekendShiftsInCycle = useMemo(() => {
		if (!nurse || !nurse.prnWeekendShiftsInCycle) return "0";
		return nurse.prnWeekendShiftsInCycle.toFixed(0);
	}, [nurse]);
	const onPrnWeekendShiftsInCycleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const prnWeekendShiftsInCycle = Number.parseInt(event.target.value);
			if (!Number.isNaN(prnWeekendShiftsInCycle) && prnWeekendShiftsInCycle > 0) {
				setNurseData((prevState) => ({
					...prevState,
					prnWeekendShiftsInCycle: prnWeekendShiftsInCycle,
				}));
			} else {
				setNurseData((prevState) => ({
					...prevState,
					prnWeekendShiftsInCycle: 0,
				}));
			}
		},
		[setNurseData],
	);

	const nursingStartingDate = useMemo(() => {
		if (!nurse || !nurse.nursingStartingDate) return "";
		return nurse.nursingStartingDate.toFormat("yyyy-MM-dd");
	}, [nurse]);
	const onNursingStartingDateChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const newDate = DateTime.fromFormat(event.target.value, "yyyy-MM-dd");
			if (newDate.isValid) {
				setNurseData((prevState) => ({
					...prevState,
					nursingStartingDate: newDate,
				}));
			}
		},
		[setNurseData],
	);

	const selectedTrainingStatusType: TrainingStatusKeys = useMemo(
		() =>
			nurse
				? (
						nurseTrainingStatusDropdownItems.find((value) => value.value === getEnumKeyByValue(NurseTrainingStatus, nurse.trainingStatus)) ??
						nurseTrainingStatusDropdownItems[0]
					).value
				: nurseTrainingStatusDropdownItems[0].value,
		[nurse],
	);
	const onTrainingStatusChange = useCallback(
		(newTrainingStatus?: TrainingStatusKeys) => {
			if (newTrainingStatus) {
				setNurseData((prevState) => ({
					...prevState,
					trainingStatus: NurseTrainingStatus[newTrainingStatus],
				}));
			}
			trackEvent("profile_training_type_change", {
				training_status: newTrainingStatus,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id],
	);

	const canBeReliefChargeValue = useMemo(() => {
		if (!nurse) return false;
		return nurse.canBeReliefCharge ?? false;
	}, [nurse]);

	const onCanBeReliefChargeChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setNurseData((prevState) => ({
				...prevState,
				canBeReliefCharge: event.target.checked,
			}));
			trackEvent("profile_shift_manager_switch_change", {
				can_be_relief_charge: event.target.checked,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id],
	);

	const permanentShiftManagerValue = useMemo(() => {
		if (!nurse) return false;
		return nurse.isShiftManager ?? false;
	}, [nurse]);

	const onPermanentShiftManagerChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setNurseData((prevState) => ({
				...prevState,
				isShiftManager: event.target.checked,
			}));
			trackEvent("profile_permanent_shift_manager_switch_change", {
				can_be_relief_charge: event.target.checked,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id],
	);

	const excludeFromPpdValue = useMemo(() => {
		if (!nurse) return false;
		return nurse.excludeFromPpd ?? false;
	}, [nurse]);

	const onExcludeFromPpdChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setNurseData((prevState) => ({
				...prevState,
				excludeFromPpd: event.target.checked,
			}));
			trackEvent("profile_exclude_from_ppd_switch_change", {
				exclude_from_ppd: event.target.checked,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id],
	);

	const ignoreFromScheduleValue = useMemo(() => {
		if (!nurse) return false;
		return nurse.ignoreFromSchedule ?? false;
	}, [nurse]);

	const onIgnoreFromScheduleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setNurseData((prevState) => ({
				...prevState,
				ignoreFromSchedule: event.target.checked,
			}));
			trackEvent("profile_ignore_from_schedule_switch_change", {
				ignore_from_schedule: event.target.checked,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id],
	);

	const doNotDisturbCallingValue = useMemo(() => {
		if (!nurse) return false;
		return nurse.doNotDisturbCalling ?? false;
	}, [nurse]);

	const onDoNotDisturbCallingChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setNurseData((prevState) => ({
				...prevState,
				doNotDisturbCalling: event.target.checked,
			}));
			trackEvent("profile_do_not_disturb_calling_switch_change", {
				do_not_disturb_calling: event.target.checked,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id],
	);

	const autoApproveEligibleValue = useMemo(() => {
		if (!nurse) return true;
		// DEFAULT: an unset autoApproveEligible means the nurse is eligible (matches the backend default true).
		return nurse.autoApproveEligible ?? true;
	}, [nurse]);

	const onAutoApproveEligibleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setNurseData((prevState) => ({
				...prevState,
				autoApproveEligible: event.target.checked,
			}));
			trackEvent("profile_auto_approve_eligible_switch_change", {
				auto_approve_eligible: event.target.checked,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id],
	);

	const unionValue = useMemo(() => nurse?.union ?? "", [nurse]);

	// Membership intent read off the edit state — see `isUnionMemberInEditState` for why this is not
	// the trimmed-truthiness test the read-only surfaces use (APP-13).
	const isUnionMember = isUnionMemberInEditState(nurse?.union);

	const onUnionMemberChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const checked = event.target.checked;
			setNurseData((prevState) => ({
				...prevState,
				union: checked ? prevState.union || "" : null,
			}));
			track("profile_union_member_switch_change", {
				is_union_member: checked,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id, track],
	);

	const onUnionNameChange = useCallback(
		(_: React.SyntheticEvent, unionName: string, __: AutocompleteInputChangeReason) => {
			setNurseData((prevState) =>
				prevState.union != null
					? {
							...prevState,
							union: unionName,
						}
					: prevState,
			);
		},
		[setNurseData],
	);

	const selectedAgency = useMemo(() => nurse?.agencyName ?? "", [nurse]);

	const onAgencyChange = useCallback(
		(newAgency?: string) => {
			// Update nurse data
			setNurseData((prevState) => ({
				...prevState,
				agencyName: newAgency,
			}));

			if (newAgency && nurse?.role && nurse.employmentType && isAgencyNurse(nurse.employmentType)) {
				setAgencyForWageFetch({ agencyName: newAgency, role: nurse.role });
			}

			trackEvent("profile_agency_change", {
				agency: newAgency,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id, nurse?.role, nurse?.employmentType],
	);

	const displayWageValue = useMemo(() => {
		if (nurse?.baseWages && nurse.baseWages.length === 0) {
			return "";
		}
		return !isEditable && wageValue.length === 0 ? "0" : wageValue;
	}, [nurse?.baseWages, wageValue, isEditable]);

	const selectedHomeDepartmentId = nurse?.homeDepartmentId;
	const additionalFacilities = nurse?.departments.filter((deptId) => deptId !== selectedHomeDepartmentId) || [];

	const onHomeDepartmentChange = useCallback(
		(newHomeDepartmentId?: string) => {
			if (newHomeDepartmentId) {
				setNurseData((prevState) => ({
					...prevState,
					homeDepartmentId: newHomeDepartmentId,
					departments: [newHomeDepartmentId, ...prevState.departments.filter((deptId) => deptId !== newHomeDepartmentId)],
				}));
			}
			track("profile_home_facility_change", {
				home_department_id: newHomeDepartmentId,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id, track],
	);

	// All facilities from same hospital (excluding home department)
	const allFacilitiesFromSameHospital = useMemo(() => {
		if (!userInfo.departments || !chosenDepartment) return [];
		return userInfo.departments.filter((dept) => dept.hospital === chosenDepartment.hospital && dept.department_id !== selectedHomeDepartmentId);
	}, [userInfo.departments, chosenDepartment, selectedHomeDepartmentId]);

	// Job selection — all jobs (excluding "unknown"), fetched from GET /jobs
	const { data: allJobs = [] } = useQuery({
		queryKey: ["jobs"],
		queryFn: async () => {
			const res = await backendService.getJobs();
			return res.data;
		},
		staleTime: 5 * 60 * 1000, // 5 minutes — jobs are a static list, rarely changes
		enabled: isEditable,
	});

	const departmentJobIds = useMemo(() => new Set(chosenDepartment.jobIds ?? []), [chosenDepartment.jobIds]);
	const availableJobs = useMemo(
		() => allJobs.filter((j) => j.name !== "unknown" && departmentJobIds.has(j.id)).sort((a, b) => a.displayName.localeCompare(b.displayName)),
		[allJobs, departmentJobIds],
	);

	const selectedJobIds = useMemo(() => nurse?.jobIds ?? [], [nurse?.jobIds]);

	const hasEmployeesHris = useMemo(() => hasHrisIntegrationOfType(chosenDepartment, "employees"), [chosenDepartment]);
	const {
		data: jobRecommendations,
		isError: isJobRecommendationsError,
		error: jobRecommendationsError,
	} = useJobRecommendations({ enabled: isEditable && !hasEmployeesHris });

	const { jobs: recommendedJobs, isSatisfied: isRecommendationSatisfied } = useMemo(
		() => resolveRecommendedJobs(jobRecommendations, selectedRole ? NurseFullRole[selectedRole] : undefined, availableJobs, selectedJobIds),
		[jobRecommendations, selectedRole, availableJobs, selectedJobIds],
	);

	const showJobRecommendation = isEditable && !hasEmployeesHris && !!selectedRole && recommendedJobs.length > 0 && !isRecommendationSatisfied;

	const recommendedJobIdsKey = useMemo(() => recommendedJobs.map((job) => job.id).join(","), [recommendedJobs]);

	useEffect(() => {
		if (!showJobRecommendation) return;
		track("profile_job_recommendation_viewed", {
			department_id: chosenDepartment.department_id,
			role: selectedRole ? NurseFullRole[selectedRole] : undefined,
			recommended_job_ids: recommendedJobIdsKey,
			recommended_job_count: recommendedJobs.length,
			is_new_profile: !nurse?.id,
		});
		// Keyed on the recommendation itself, so this fires once per (role, recommendation) rather
		// than on every re-render of the form.
	}, [showJobRecommendation, recommendedJobIdsKey, recommendedJobs.length, selectedRole, chosenDepartment.department_id, nurse?.id, track]);

	useEffect(() => {
		if (!isJobRecommendationsError) return;
		// A failed hint doesn't warrant a snackbar, but it must not vanish silently either.
		trackError("profile_job_recommendation_fetch", {
			department_id: chosenDepartment.department_id,
			error_message: (jobRecommendationsError as Error | null)?.message,
		});
	}, [isJobRecommendationsError, jobRecommendationsError, chosenDepartment.department_id, trackError]);

	const onJobsChange = useCallback(
		(newJobIds: string[]) => {
			// The "at least one job" rule is enforced softly by the dropdown itself
			// (minimalSelection): the user may deselect down to 0 while it's open, and it
			// restores the last valid selection + warns on close. So we must NOT drop an
			// empty update here, or the deselect can never take visible effect.
			// Preserve existing order: keep current IDs in place, append new ones at the end
			const currentIds = nurse?.jobIds ?? [];
			const kept = currentIds.filter((id) => newJobIds.includes(id));
			const added = newJobIds.filter((id) => !currentIds.includes(id));
			const orderedJobIds = [...kept, ...added];
			setNurseData((prevState) => ({
				...prevState,
				jobIds: orderedJobIds,
			}));
			track("profile_jobs_change", {
				job_ids: orderedJobIds.join(","),
				nurse_id: nurse?.id,
				recommended_job_ids: recommendedJobIdsKey,
				followed_recommendation: recommendedJobs.length > 0 && recommendedJobs.every((job) => orderedJobIds.includes(job.id)),
			});
		},
		[setNurseData, nurse?.id, nurse?.jobIds, track, recommendedJobIdsKey, recommendedJobs],
	);

	// DAT-586: one-click "Apply" on the recommendation hint. Goes through `onJobsChange` rather than
	// writing `jobIds` directly, so the applied selection carries the same ordering rules and the same
	// `profile_jobs_change` tracking as a manual pick — the analytics can then tell an applied
	// recommendation from a hand-assembled one by pairing the two events.
	const onApplyRecommendedJobs = useCallback(() => {
		if (!recommendedJobs.length) return;
		track("profile_job_recommendation_applied", {
			department_id: chosenDepartment.department_id,
			role: selectedRole ? NurseFullRole[selectedRole] : undefined,
			recommended_job_ids: recommendedJobIdsKey,
			previously_selected_job_ids: selectedJobIds.join(","),
			is_new_profile: !nurse?.id,
		});
		onJobsChange(applyRecommendedJobs(selectedJobIds, recommendedJobs));
	}, [recommendedJobs, recommendedJobIdsKey, selectedJobIds, onJobsChange, track, chosenDepartment.department_id, selectedRole, nurse?.id]);

	const onSetPrimaryJob = useCallback(
		(jobId: string) => {
			const currentIds = nurse?.jobIds ?? [];
			if (!currentIds.includes(jobId)) return;
			const reordered = [jobId, ...currentIds.filter((id) => id !== jobId)];
			setNurseData((prevState) => ({
				...prevState,
				jobIds: reordered,
			}));
			track("profile_primary_job_change", {
				primary_job_id: jobId,
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id, nurse?.jobIds, track],
	);

	const onAdditionalFacilitiesChange = useCallback(
		(newAdditionalFacilityIds: string[]) => {
			const updatedDepartments = [chosenDepartment.department_id, ...newAdditionalFacilityIds];
			setNurseData((prevState) => {
				// Reactivate any department that is being re-added
				const updatedStatuses = { ...prevState.departmentStatuses };
				for (const deptId of newAdditionalFacilityIds) {
					updatedStatuses[deptId] = true;
				}
				return {
					...prevState,
					departments: updatedDepartments,
					departmentStatuses: updatedStatuses,
				};
			});
			track("profile_additional_facilities_change", {
				additional_facilities: newAdditionalFacilityIds.join(","),
				nurse_id: nurse?.id,
			});
		},
		[setNurseData, nurse?.id, chosenDepartment.department_id, track],
	);

	return {
		selectedRole,
		onRoleChange,
		selectedContractType,
		onContractTypeChange,
		selectedWageType,
		onWageTypeChange,
		wageValue: displayWageValue,
		onWageChange,
		nursingStartingDate,
		onNursingStartingDateChange,
		selectedTrainingStatusType,
		onTrainingStatusChange,
		canBeReliefChargeValue,
		onCanBeReliefChargeChange,
		permanentShiftManagerValue,
		onPermanentShiftManagerChange,
		excludeFromPpdValue,
		onExcludeFromPpdChange,
		ignoreFromScheduleValue,
		onIgnoreFromScheduleChange,
		doNotDisturbCallingValue,
		onDoNotDisturbCallingChange,
		autoApproveEligibleValue,
		onAutoApproveEligibleChange,
		availableRoles,
		prnShiftsInCycle,
		onPrnShiftsInCycleChange,
		prnWeekendShiftsInCycle,
		onPrnWeekendShiftsInCycleChange,
		isWageHiddenForCurrentUser: !canViewWage,
		baseWage,
		selectedAgency,
		onAgencyChange,
		fetchingAgencyWage,
		selectedHomeDepartmentId,
		onHomeDepartmentChange,
		additionalFacilities,
		allFacilitiesFromSameHospital,
		onAdditionalFacilitiesChange,
		isSupportUser: userInfo.support,
		userDepartments: userInfo.departments,
		unionValue,
		isUnionMember,
		onUnionMemberChange,
		onUnionNameChange,
		agencies,
		existingUnions,
		existingUnionsLoading,
		availableJobs,
		createAgency,
		selectedJobIds,
		onJobsChange,
		recommendedJobs,
		showJobRecommendation,
		onApplyRecommendedJobs,
		onSetPrimaryJob,
		showJobReminderDialog,
		setShowJobReminderDialog,
	};
};
